import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { TFunction } from 'i18next'
import useConfig from 'modules/config/state/use-config'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetTokenexConfigQuery } from 'redux/services/tokenex'
import {
  TokenexHookProps,
  TokenexIframe,
  TokenexOnCardTypeChangeData,
  TokenexTokenizeData,
  TokenexValidateData,
} from './tokenex-types'
import { styles } from './tokenex.styled'

const errorLabels = (t: TFunction<'translation', undefined>) => ({
  customRegEx: {
    ACH: t('TokenexInput.errorLabels.customRegEx.ACH', 'Please enter a valid account number.'),
    CC: t('TokenexInput.errorLabels.customRegEx.CC', 'Card number is invalid.'),
    CVC: t('TokenexInput.errorLabels.customRegEx.CVC', 'CVC is invalid.'),
  },
  format: {
    ACH: t('TokenexInput.errorLabels.format.ACH', 'Please enter a valid account number.'),
    CC: t('TokenexInput.errorLabels.format.CC', 'Card number is invalid.'),
    CVC: t('TokenexInput.errorLabels.format.CVC', 'CVC is invalid.'),
  },
  required: {
    ACH: t('TokenexInput.errorLabels.required.ACH', 'Account number is required.'),
    CC: t('TokenexInput.errorLabels.required.CC', 'Card number is required.'),
    CVC: t('TokenexInput.errorLabels.required.CVC', 'CVC is required.'),
  },
})

/**
 * Tokenex Documentation: https://documentation.ixopay.com/docs/tokenex/using-the-iframe
 */
export const useTokenex = ({
  type,
  id,
  cvvContainerID,
  label,
  onBlur,
  onToken,
  tokenizeOnBlur = false,
}: TokenexHookProps) => {
  // Store callbacks in refs to avoid dependency issues
  const onBlurRef = useRef(onBlur)
  const onTokenRef = useRef(onToken)

  useEffect(() => {
    onBlurRef.current = onBlur
  }, [onBlur])

  useEffect(() => {
    onTokenRef.current = onToken
  }, [onToken])

  const [tokenexIframe, setTokenexIframe] = useState<TokenexIframe | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  const [isFocused, setIsFocused] = useState(false)
  const [isCvvFocused, setIsCvvFocused] = useState(false)

  const [tokenexError, setTokenexError] = useState<string>()
  const [cvvError, setCvvError] = useState<string>()

  const [cardBrand, setCardBrand] = useState<string>()

  const [token, setToken] = useState<string>()

  const { t } = useTranslation()
  const config = useConfig()
  const origin = encodeURIComponent(window.location.origin)

  const { data, isSuccess } = useGetTokenexConfigQuery({
    campaignId: config.campaignId,
    origin,
    type,
  })

  const localErrorLabels = useMemo(() => errorLabels(t), [t])

  const tokenexConfig = useMemo(() => {
    if (!data || !isSuccess) return undefined

    const config = { ...data, styles }
    config.customDataLabel = label

    if (type === 'CC') {
      config.enablePrettyFormat = true
      config.placeholder = '1234 1234 1234 1234'
      config.cvvContainerID = cvvContainerID
      config.cvv = true
      config.cvvPlaceholder = 'CVC'
      config.enableAutoComplete = true
    }

    return config
  }, [cvvContainerID, data, isSuccess, label, type])

  useEffect(() => {
    if (!window.TokenEx) return

    if (tokenexConfig) {
      const TokenexIframe = new window.TokenEx.Iframe(id, tokenexConfig)

      const handleValidate = (data: TokenexValidateData) => {
        const { isValid, validator, isCvvValid, cvvValidator } = data

        if (cvvValidator && !isCvvValid) {
          const message = localErrorLabels[cvvValidator]['CVC']
          setCvvError(message)
        } else {
          setCvvError(undefined)
        }

        if (isValid) {
          setTokenexError(undefined)
        } else if (validator) {
          const message = localErrorLabels[validator][type]
          setTokenexError(message)
        }
      }

      const handleTokenize = (data: TokenexTokenizeData) => {
        const { token, cardType } = data

        if (!!cardType) {
          setCardBrand(cardType)
        }

        onTokenRef.current?.(token)
        setToken(token)
      }

      TokenexIframe.on('validate', handleValidate)
      TokenexIframe.on('tokenize', handleTokenize)

      TokenexIframe.on('focus', () => {
        setIsFocused(true)
      })

      TokenexIframe.on('blur', () => {
        setIsFocused(false)
        onBlurRef.current?.()

        if (tokenexError) {
          //re-validate to clear error
          TokenexIframe.validate()
        }

        if (tokenizeOnBlur) {
          TokenexIframe.validate()

          if (!tokenexError && !cvvError) {
            TokenexIframe.tokenize()
          }
        }
      })

      TokenexIframe.on('cvvFocus', () => {
        setIsCvvFocused(true)
      })

      TokenexIframe.on('cvvBlur', function () {
        setIsCvvFocused(false)

        if (cvvError) {
          //re-validate to clear error
          TokenexIframe.validate()
        }
      })

      TokenexIframe.on('error', (e: NotifiableError) => {
        console.error(e)
        Bugsnag.notify(e)
      })

      TokenexIframe.on('load', () => {
        setIsLoading(false)
      })

      if (type === 'CC') {
        TokenexIframe.on('cardTypeChange', ({ possibleCardType }: TokenexOnCardTypeChangeData) => {
          setCardBrand(possibleCardType)
        })
      }

      TokenexIframe.load()
      setTokenexIframe(TokenexIframe)
    }
  }, [tokenexConfig, id, type, label, localErrorLabels, tokenexError, cvvError, tokenizeOnBlur])

  return {
    id,
    label,
    tokenexIframe,
    token,
    isLoading,
    isFocused,
    isCvvFocused,
    tokenexError,
    cvvError,
    cardBrand,
  }
}
